import { parseISO } from 'date-fns/parseISO';
import dateFormats from 'month-view-page-common/dateFormats';
import Localisation, {
  retrieval,
  currency as currencyFeatures,
  date as dateFeatures,
  number as numberFeatures,
} from 'saddlebag-localisation';

import type { DayOfWeek, CldrInfo } from '../types/culture';
import type { Translations } from '../types/localisation';
import type { LocalisationType } from 'saddlebag-localisation';

const parseDateTime = (datetime: string | Date): Date => {
  if (typeof datetime === 'string') {
    return parseISO(datetime);
  }
  return datetime;
};

export type LocalisationConfig = {
  currency: string;
  locale: string;
  market: string;
};

export type LocalisationHelpers = {
  formatMonth: (date: Date) => string;
  formatDateFull: (date: Date) => string;
  formatDateAbbr: (date: Date) => string;
  formatDateFullAbbr: (date: Date) => string;
  formatMonthDay: (date: Date) => string;
  formatTime: (date: Date) => string;
  formatCurrency: (value: number) => string;
  formatCurrencyShort: (value: number) => string;
  formatNumber: (value: number) => string;
  daysOfWeek: DayOfWeek[];
  locale: string;
  market: string;
  currency: string;
  firstDayOfWeek: number;
  translations: Record<string, string>;
};

let localisation: LocalisationType;

const localisationInit = (
  cldr: CldrInfo,
  { currency, locale, market }: LocalisationConfig,
  translations: Translations,
): LocalisationHelpers => {
  Localisation.enhance(
    retrieval,
    currencyFeatures,
    dateFeatures,
    numberFeatures,
  );
  localisation = Localisation(cldr);
  const daysOfWeek = localisation.getDaysOfWeek();
  const firstDayOfWeek = localisation.getFirstDay();
  return {
    formatMonth: (date) => localisation.formatDate(date, 'yearMonth'),
    formatDateFull: (date) => localisation.formatDate(date, 'full'),
    formatDateAbbr: (date) => localisation.formatDate(date, 'monthDayAbbr'),
    formatDateFullAbbr: (date) => localisation.formatDate(date, 'fullAbbr'),
    formatMonthDay: (date) => localisation.formatDate(date, 'monthDay'),
    formatTime: (date) => localisation.formatTime(date),
    formatCurrency: (value) => localisation.formatCurrency(value),
    formatCurrencyShort: (value) =>
      localisation.formatCurrencyShort(value, currency),
    formatNumber: (value) => localisation.formatNumber(value),
    daysOfWeek,
    locale,
    market,
    currency,
    firstDayOfWeek,
    translations: {
      ...translations.inspiration,
      ...translations.flightsFunnel,
    },
  };
};

// formatDateNonlocalised as util function where we only need format date so not need passing by props
// a default value to format as 'yyyy-MM-dd'
export const formatDateNonlocalised = (
  date: string | Date,
  format = dateFormats.YEAR_MONTH_DAY_DASHED,
) => localisation.formatDateNonlocalised(parseDateTime(date), format);

export const formatDate = (date: Date, format: string) =>
  localisation.formatDate(date, format);
export const getFirstDay = () => localisation.getFirstDay();
export const getDaysOfWeek = () => localisation.getDaysOfWeek();

export default localisationInit;
